// WbBridgeTile — Phase C13.
//
// One tile per active weighbridge on the dashboard. Two responsibilities:
//   1. Render the bridge's status badge (online / offline / maintenance).
//   2. When the mock-agent flag is on AND the bridge is online, open an
//      EventSource to /api/v1/wb/mock-agent/stream/:enc_id and surface the
//      latest live weight + stable indicator.
//
// When the flag is off (production / unset), no EventSource is constructed
// and we render "--" with a hint that live data flows through the agent.
//
// Lifecycle: each tile owns its own EventSource and closes it on unmount
// or when its enc_id / online-status changes. With N tiles this means N
// open connections in dev — accepted; production has the flag off and
// opens no connections.

import { useEffect, useRef, useState } from 'react';
import { Badge, type BadgeVariant } from '../../../components/ui/Badge/Badge';
import { deriveWbDisplayStatus, type WbWeighbridge } from '../hooks/useWbWeighbridges';
import styles from './WbBridgeTile.module.css';

const STATUS_VARIANT: Record<WbWeighbridge['status'], BadgeVariant> = {
  online: 'success',
  offline: 'danger',
  maintenance: 'warning',
};

const STATUS_LABEL: Record<WbWeighbridge['status'], string> = {
  online: 'Online',
  offline: 'Offline',
  maintenance: 'Maintenance',
};

const KG_FORMATTER = new Intl.NumberFormat('en-GB', {
  maximumFractionDigits: 1,
});

interface MockAgentWeightPayload {
  weight_kg?: number | string | null;
  is_stable?: boolean | null;
}

interface LiveWeight {
  kg: number;
  stable: boolean;
}

export interface WbBridgeTileProps {
  weighbridge: WbWeighbridge;
}

export function WbBridgeTile({ weighbridge }: WbBridgeTileProps): JSX.Element {
  const mockAgentMode =
    (import.meta.env.VITE_WB_MOCK_AGENT_MODE as string | undefined) === 'true';
  const useSse = mockAgentMode && weighbridge.status === 'online';

  const [live, setLive] = useState<LiveWeight | null>(null);
  const esRef = useRef<EventSource | null>(null);

  useEffect(() => {
    if (!useSse) {
      if (esRef.current !== null) {
        esRef.current.close();
        esRef.current = null;
      }
      setLive(null);
      return;
    }

    const url = `/api/v1/wb/mock-agent/stream/${encodeURIComponent(weighbridge.enc_id)}`;
    let es: EventSource;
    try {
      es = new EventSource(url);
    } catch {
      return;
    }
    esRef.current = es;

    function handleWeight(ev: MessageEvent) {
      try {
        const data = JSON.parse(ev.data as string) as MockAgentWeightPayload;
        const kg =
          typeof data.weight_kg === 'number'
            ? data.weight_kg
            : typeof data.weight_kg === 'string'
              ? Number(data.weight_kg)
              : null;
        if (kg !== null && Number.isFinite(kg)) {
          setLive({ kg, stable: data.is_stable === true });
        }
      } catch {
        // Drop malformed payloads — the dev mock is best-effort.
      }
    }

    es.addEventListener('weight', handleWeight as EventListener);
    return () => {
      es.removeEventListener('weight', handleWeight as EventListener);
      es.close();
      if (esRef.current === es) {
        esRef.current = null;
      }
    };
  }, [useSse, weighbridge.enc_id]);

  const calibration = weighbridge.current_calibration;
  const displayStatus = deriveWbDisplayStatus(weighbridge);

  return (
    <section
      className={styles.tile}
      data-testid={`wb-bridge-tile-${weighbridge.enc_id}`}
    >
      <header className={styles.head}>
        <div className={styles.headText}>
          <h3 className={styles.name}>{weighbridge.name}</h3>
          <p className={styles.yard}>{weighbridge.yard.name}</p>
        </div>
        <Badge variant={STATUS_VARIANT[displayStatus]}>
          {STATUS_LABEL[displayStatus]}
        </Badge>
      </header>

      <div className={styles.readout}>
        {useSse && live !== null ? (
          <>
            <span className={styles.weightRow}>
              <span
                className={`${styles.dot} ${
                  live.stable ? styles.dotStable : styles.dotUnstable
                }`}
                aria-hidden
              />
              <span className={styles.weight}>
                {KG_FORMATTER.format(live.kg)}
                <span className={styles.unit}>kg</span>
              </span>
            </span>
            <span className={styles.hint}>
              {live.stable ? 'Stable' : 'Reading…'}
            </span>
          </>
        ) : useSse ? (
          <>
            <span className={styles.weight}>
              <span className={styles.placeholder}>--</span>
              <span className={styles.unit}>kg</span>
            </span>
            <span className={styles.hint}>Awaiting first reading…</span>
          </>
        ) : (
          <>
            <span className={styles.weight}>
              <span className={styles.placeholder}>--</span>
              <span className={styles.unit}>kg</span>
            </span>
            <span className={styles.hint}>Live data via on-prem agent</span>
          </>
        )}
      </div>

      {calibration !== null ? (
        <footer className={styles.footer}>
          <span className={styles.metaLabel}>Calibration valid until</span>
          <span className={styles.metaValue}>{calibration.valid_until}</span>
        </footer>
      ) : null}
    </section>
  );
}
